/**
 * EngagementsResiliationsList — Liste résiliations + éditeur lettre + envoi LRAR.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FileText, Send, Loader2, CheckCircle, Clock, Eye, Edit3, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/engagements-categories";

const STATUT_BADGE = {
  brouillon: { c: "bg-slate-200 text-slate-700", l: "Brouillon" },
  envoye_email: { c: "bg-blue-100 text-blue-700", l: "Envoyé email" },
  envoye_lrar: { c: "bg-emerald-100 text-emerald-700", l: "Envoyé LRAR" },
  confirme: { c: "bg-emerald-600 text-white", l: "Confirmée (lettre reçue)" },
};

export default function EngagementsResiliationsList() {
  const [params] = useSearchParams();
  const [resils, setResils] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(true);
  const [confirmLrar, setConfirmLrar] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptDate, setAcceptDate] = useState("");
  const [acceptFile, setAcceptFile] = useState(null);
  const [uploadingAccept, setUploadingAccept] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/engagements/resiliations");
      setResils(r.data.resiliations || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const id = params.get("id");
    if (id && resils.length) {
      const r = resils.find(x => x.id === id);
      if (r) { setSelected(r); setContent(r.lettre_contenu || ""); }
    }
  }, [params, resils]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/engagements/resiliations/${selected.id}`, { lettre_contenu: content });
      toast.success("Lettre sauvegardée");
      setPreview(true); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const sendLrar = async () => {
    if (!selected) return;
    setSending(true); setConfirmLrar(false);
    try {
      const r = await api.post(`/engagements/resiliations/${selected.id}/send-lrar`);
      toast.success(`LRAR envoyée · Suivi ${r.data.numero_suivi}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const uploadAcceptance = async () => {
    if (!selected) return;
    if (!acceptDate && !acceptFile) return toast.error("PDF ou date requis");
    setUploadingAccept(true);
    try {
      const fd = new FormData();
      if (acceptFile) fd.append("file", acceptFile);
      if (acceptDate) fd.append("date_effet", acceptDate);
      const r = await api.post(`/engagements/resiliations/${selected.id}/acceptance`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      const src = r.data.date_source;
      const detected = r.data.ocr_date_detected;
      const msg = src === "ocr"
        ? `Acceptation enregistrée · Date d'effet ${r.data.date_effet_resiliation} (détectée par IA)`
        : `Acceptation enregistrée · Date d'effet ${r.data.date_effet_resiliation}${detected && detected !== r.data.date_effet_resiliation ? ` (IA suggérait ${detected})` : ''}`;
      toast.success(msg);
      setAcceptOpen(false); setAcceptDate(""); setAcceptFile(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploadingAccept(false); }
  };

  const deleteResiliation = async () => {
    if (!selected) return;
    if (!window.confirm("Supprimer cette lettre brouillon ?\n\nLe contrat sera repassé en 'actif' — vous pourrez régénérer une nouvelle lettre.")) return;
    try {
      await api.delete(`/engagements/resiliations/${selected.id}`);
      toast.success("Lettre supprimée · contrat réactivé");
      setSelected(null); setContent("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="resiliations-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Résiliations</h1>
        <p className="text-sm text-slate-500">Lettres générées par IA · Envoi LRAR via La Poste</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-sm">Résiliations ({resils.length})</CardTitle></CardHeader>
          <CardContent className="p-2 max-h-[600px] overflow-y-auto">
            {loading ? <p className="p-4 text-slate-500 text-sm">Chargement...</p> :
             resils.length === 0 ? <p className="p-4 text-slate-500 text-sm text-center">Aucune résiliation</p> :
             resils.map(r => {
               const b = STATUT_BADGE[r.statut_envoi] || STATUT_BADGE.brouillon;
               return (
                 <div key={r.id} onClick={() => { setSelected(r); setContent(r.lettre_contenu || ""); setPreview(true); }}
                          className={`p-3 rounded-lg cursor-pointer border ${selected?.id === r.id ? "border-blue-500 bg-blue-50" : "border-transparent hover:bg-slate-50"}`}
                          data-testid={`resil-item-${r.id}`}>
                   <p className="text-sm font-medium truncate">{r.contrat_nom || "Contrat"}</p>
                   <p className="text-xs text-slate-500 truncate">{r.prestataire}</p>
                   <div className="flex items-center gap-2 mt-1">
                     <Badge className={`${b.c} text-[10px]`}>{b.l}</Badge>
                     <span className="text-[10px] text-slate-400">{fmtDate(r.created_at)}</span>
                   </div>
                 </div>
               );
             })}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" />{selected ? "Aperçu" : "Sélectionnez une lettre"}</CardTitle>
            {selected && (
              <div className="flex gap-2">
                {preview ? (
                  <Button size="sm" variant="outline" onClick={() => setPreview(false)} data-testid="edit-letter-btn"><Edit3 className="w-4 h-4 mr-1" />Modifier</Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setPreview(true)}><Eye className="w-4 h-4 mr-1" />Aperçu</Button>
                )}
                {!preview && <Button size="sm" onClick={save} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}Sauver</Button>}
                {selected.statut_envoi === "brouillon" && preview && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setConfirmLrar(true)} data-testid="send-lrar-btn"><Send className="w-4 h-4 mr-1" />Envoyer LRAR</Button>
                )}
                {selected.statut_envoi && !["confirme"].includes(selected.statut_envoi) && preview && (
                  <Button size="sm" variant="outline" onClick={() => setAcceptOpen(true)} data-testid="upload-acceptance-btn">
                    <CheckCircle className="w-4 h-4 mr-1" />Acceptation reçue
                  </Button>
                )}
                {selected.statut_envoi === "brouillon" && (
                  <Button size="sm" variant="outline" onClick={deleteResiliation} data-testid="delete-resil-btn"
                              className="border-red-300 text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1" />Supprimer
                  </Button>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selected ? <p className="text-center text-slate-500 py-12">Aucune lettre sélectionnée</p> :
             preview ? (
               <div className="bg-white border rounded-lg p-6 whitespace-pre-line font-mono text-sm text-slate-800 max-h-[600px] overflow-y-auto">{content}</div>
             ) : (
               <Textarea value={content} onChange={e => setContent(e.target.value)} rows={24} className="font-mono text-xs" data-testid="letter-textarea" />
             )}
            {selected?.numero_suivi_laposte && (
              <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />Numéro de suivi La Poste : <span className="font-mono font-bold">{selected.numero_suivi_laposte}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={confirmLrar} onOpenChange={setConfirmLrar}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirmer l'envoi LRAR</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <p className="text-sm text-slate-600">L'envoi sera effectué via l'API La Poste (Okapi). Une fois envoyé, un numéro de suivi vous sera fourni.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmLrar(false)}>Annuler</Button>
            <Button onClick={sendLrar} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent data-testid="acceptance-dialog">
          <DialogHeader>
            <DialogTitle>Confirmation de résiliation reçue du fournisseur</DialogTitle>
            <DialogDescription>Uploadez la lettre d'acceptation reçue de votre fournisseur. L'IA détecte automatiquement la date d'effet — vous pouvez la corriger si besoin. Le contrat sera automatiquement marqué « résilié » à cette date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Lettre d'acceptation fournisseur (PDF) *</label>
              <input type="file" accept="application/pdf,image/*"
                          onChange={e => setAcceptFile(e.target.files?.[0] || null)}
                          className="w-full mt-1 text-xs" data-testid="accept-file-input" />
              {acceptFile && <p className="text-xs text-slate-500 mt-1">📄 {acceptFile.name}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Date d'effet de la résiliation *</label>
              <input type="date" value={acceptDate} onChange={e => setAcceptDate(e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md" data-testid="accept-date-input"
                          placeholder="Détectée par OCR si laissée vide" />
              <p className="text-[10px] text-slate-400 mt-1">Laissez vide pour laisser l'IA la détecter dans le PDF</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptOpen(false)}>Annuler</Button>
            <Button onClick={uploadAcceptance} disabled={uploadingAccept || (!acceptDate && !acceptFile)} data-testid="confirm-acceptance-btn">
              {uploadingAccept ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
